/**
 * The server's readiness cut, once the start has synced (D148, contracts §1).
 *
 * The session runs on the local preview (`readiness-preview.ts`) until the
 * server has answered `POST /me/sessions`. The outbox runner keeps no answer
 * — it only marks the item done — so the answer is read by REPLAYING the
 * start: the same body, the same client id. The server answers a replay with
 * the stored session and its adjustments and writes nothing (T26), so this
 * costs one request per session and changes nothing on the server.
 *
 * ── WHEN, EXACTLY ──────────────────────────────────────────────────────────
 * Only when the start item is no longer in the queue: sent and done. While it
 * is queued (pending, in flight or quarantined) a direct POST would overtake
 * the queue. And never after the person DISCARDED a quarantined start: a
 * replay of a start the server never stored would create it. The quarantine
 * sheet settles the session before it discards its start (`settleAdjustments`
 * with `null`, which keeps the preview), and a settled session is never
 * replayed.
 */
import type { OutboxState } from "@repo/client/offline";
import type {
  SessionAdjustment,
  SessionStarted,
  SessionStartInput,
} from "@repo/contract/training";
import { startEntry } from "./outbox-bridge";
import type { WorkoutSession } from "./session-model";

/** The start has left the queue and the preview is still in force. */
export function isStartReplayDue(
  session: WorkoutSession | null,
  outbox: OutboxState
): session is WorkoutSession {
  if (session === null || session.adjustmentsSettled) {
    return false;
  }
  return !outbox.items.some(
    (item) =>
      item.kind === "session.start" && item.entityId === session.sessionId
  );
}

export interface AdjustmentsSyncDeps {
  /** `startSession(api, body)` from `@repo/client/data/workout`. */
  readonly replayStart: (body: SessionStartInput) => Promise<SessionStarted>;
  readonly settle: (
    sessionId: string,
    adjustments: readonly SessionAdjustment[] | null
  ) => void;
}

/**
 * One replay. A 404 means the server does not hold the session (it belongs to
 * someone else, or was never stored): the preview stands. Any other failure
 * leaves the session unsettled for the next attempt.
 */
export async function replayStartForAdjustments(
  session: WorkoutSession,
  deps: AdjustmentsSyncDeps
): Promise<"settled" | "kept-preview" | "failed"> {
  try {
    const answer = await deps.replayStart(
      startEntry(session).body as SessionStartInput
    );
    deps.settle(session.sessionId, answer.adjustments ?? []);
    return "settled";
  } catch (error) {
    if (error instanceof Object && "status" in error && error.status === 404) {
      deps.settle(session.sessionId, null);
      return "kept-preview";
    }
    return "failed";
  }
}
